import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  ActiveElement,
  Chart,
  ChartEvent,
  ChartType,
  ChartTypeRegistry,
  registerables,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import { addMinutes, format, subMinutes } from 'date-fns';
import { BATTERY_LOW_THRESHOLD_PERCENT } from '../device-detail.constants';
import {
  BatteryHistoryViewModel,
  ChartPointViewModel,
  DeviceInfoViewModel,
  LocationSelectionViewModel,
  SensingType,
  SensingViewModel,
} from '../device-detail-summary/device-detail-summary.component';

Chart.register(...registerables);
Chart.register(zoomPlugin);

// PluginOptionsByType を使った型の拡張
declare module 'chart.js' {
  interface PluginOptionsByType<TType extends ChartType> {
    displayThresholdLine?: {
      display: boolean;
      value: {
        maxAlert?: number;
        minAlert?: number;
        maxPreAlert?: number;
        minPreAlert?: number;
      };
      color: {
        alert: string;
        preAlert: string;
      };
      lineWidth: TType extends 'line' ? number : never;
    };
  }
}

/** センサ閾値Model */
export type SensorThresholdModel = {
  AlertMax: number;
  AlertMin: number;
  PrealertMax: number;
  PrealertMin: number;
};

/** センサーModel */
export type SensingModel = {
  /** 温度 */
  temperature?: SensorThresholdModel;
  /** 気圧 */
  pressure?: SensorThresholdModel;
  /** 湿度 */
  humidity?: SensorThresholdModel;
  /** 傾斜 */
  tilt?: SensorThresholdModel;
  /** 照度 */
  light?: SensorThresholdModel;
  /** 衝撃 */
  shockDetected?: number;
};

/** センサーのオプション */
export type SensorOption = {
  label: string;
  unit: string;
};

/** 時刻設定の単位とステップサイズ */
export type UnitType = {
  unit:
    | false
    | 'minute'
    | 'hour'
    | 'day'
    | 'millisecond'
    | 'second'
    | 'week'
    | 'month'
    | 'quarter'
    | 'year'
    | undefined;
  step: number;
};

interface CustomElement {
  $context: {
    raw: {
      x: string;
      y: number;
      actualMeasurement: number | undefined;
      eventType: string | undefined;
      status: string | undefined;
    };
  };
}

type LocationChartPoint = {
  x: string;
  y: number;
  rat: string;
  locationTechType: string;
  color: string;
  locationTimeStamp: number;
  longitude: number;
  latitude: number;
};

const telemetryData = ['temperature', 'pressure', 'humidity', 'tilt', 'light'];
const BATTERY_MIN = 0;
const BATTERY_MAX = 100;
const LOCATION_Y = 110;
const POINT_RADIUS = 3;
const LOCATION_TOP_PADDING = POINT_RADIUS + 1;
const LOCATION_DEFAULT_COLOR = '#6B7280';
const LOCATION_COLORS: Record<string, string> = {
  WIFI: '#2563EB',
  GNSS: '#16A34A',
  CELL: '#000000',
  CELLULAR: '#000000',
};
const LOCATION_DATASET_LABEL = 'Location';

@Component({
  selector: 'app-device-detail-chart',
  templateUrl: './device-detail-chart.component.html',
  styleUrls: ['./device-detail-chart.component.scss'],
  standalone: true,
})
export class DeviceDetailChartComponent
  implements OnChanges, AfterViewInit, OnInit
{
  /** センサーの履歴情報 */
  @Input({ required: true }) deviceSensorHistoryList: DeviceInfoViewModel[] =
    [];

  /** バッテリー履歴 */
  @Input({ required: true }) batteryHistoryList: BatteryHistoryViewModel[] = [];

  /** Location履歴 */
  @Input({ required: true }) locationHistoryList: DeviceInfoViewModel[] = [];

  /** センサーの閾値情報 */
  @Input({ required: true }) sensorThresholdInfo: SensingModel = {};

  /** センサータイプ */
  @Input({ required: true }) sensingType: SensingType | '' | undefined;

  /** 検索フォーム */
  @Input() public historyFormGroup: FormGroup | undefined;

  /** 選択されたグラフ情報 */
  @Output() chartPointsChanged = new EventEmitter<ChartPointViewModel>();

  /** Chart.jsインスタンス */
  public chart!: Chart<
    keyof ChartTypeRegistry,
    { x: string; y: number }[],
    string | null
  >;

  /** チャート描画Canvas */
  public ctx!: CanvasRenderingContext2D;

  /** 選択されたセンサーの情報 */
  private selectedSensorHistory: SensingViewModel[] = [];

  /** センサー名と単位オブジェクト */
  private readonly sensorOptions: { [key: string]: SensorOption } = {
    temperature: { label: '温度', unit: '℃' },
    humidity: { label: '湿度', unit: '%' },
    pressure: { label: '気圧', unit: 'hPa' },
    light: { label: '照度', unit: 'lx' },
    tilt: { label: '傾斜', unit: '°' },
    fallDetected: { label: '落下検知', unit: '' },
    mcdDetected: { label: '動作検知', unit: '' },
    lightExposure: { label: '明暗', unit: 'Lux' },
    shockDetected: { label: '衝撃検知', unit: 'G' },
    battery: { label: '電源', unit: '%' },
    '': { label: '', unit: '' },
  };

  /** ステータスによる色情報 */
  private readonly markerColors: { [key: string]: string } = {
    alert: '#EF4444',
    prealert: '#FFD700',
    breach: '#4169E1',
  };

  async ngOnInit(): Promise<void> {
    const canvasElement = document.getElementById(
      'deviceDetailChart',
    ) as HTMLCanvasElement;
    this.ctx = canvasElement.getContext('2d')!;
    if (this.sensingType != null) {
      this.generateChartData(this.deviceSensorHistoryList, this.sensingType);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['deviceSensorHistoryList'] ||
        changes['batteryHistoryList'] ||
        changes['locationHistoryList'] ||
        changes['sensingType']) &&
      this.sensingType != null
    ) {
      this.generateChartData(this.deviceSensorHistoryList, this.sensingType);
    }
  }

  ngAfterViewInit(): void {
    if (this.sensingType != null) {
      this.generateChartData(this.deviceSensorHistoryList, this.sensingType);
    }
  }

  /**
   * グラフ表示用のデータを生成する
   * @param sensorHistoryList 複数センサの計測結果
   * @param sensingType 選択されているセンサータイプ
   * @returns
   */
  private generateChartData(
    sensorHistoryList: DeviceInfoViewModel[],
    sensingType: SensingType | '',
  ) {
    if (!this.ctx) {
      return;
    }

    // 既存のチャートを破棄する
    this.destroyChart();

    if (sensingType === 'battery') {
      this.selectedSensorHistory = [];
      this.createBatteryChart(this.ctx);
      return;
    }

    if (sensingType !== '') {
      this.selectedSensorHistory = sensorHistoryList
        .map((data) => data[sensingType])
        .filter(
          (sensingData): sensingData is SensingViewModel =>
            sensingData !== null && sensingData !== undefined,
        );
    }

    const { sensorHistoryData, enabled } = this.processSensorData(
      sensingType,
      this.selectedSensorHistory,
    );

    this.createChart(this.ctx, sensingType, sensorHistoryData, enabled);
  }

  /**
   * バッテリーとLocationのグラフを作成する。
   */
  private createBatteryChart(ctx: CanvasRenderingContext2D): void {
    const { batteryData, locationData } = this.createBatteryLocationChartData();
    const historyFrom = format(
      new Date(this.historyFormGroup!.value.historyFrom),
      'yyyy-MM-dd HH:mm',
    );
    const historyTo = format(
      new Date(this.historyFormGroup!.value.historyTo),
      'yyyy-MM-dd HH:mm',
    );
    const timeUnit = this.setTimeUnit(historyTo, historyFrom);

    this.chart = new Chart(ctx, {
      type: 'line',
      data: {
        datasets: [
          {
            label: 'バッテリー残量',
            data: batteryData,
            yAxisID: 'y',
            borderColor: 'rgb(111, 48, 160)',
            backgroundColor: 'rgba(111, 48, 160, 0.2)',
            borderWidth: 2,
            pointBackgroundColor: 'rgb(111, 48, 160)',
            pointRadius: POINT_RADIUS,
          },
          {
            type: 'scatter',
            label: LOCATION_DATASET_LABEL,
            data: locationData,
            yAxisID: 'y',
            backgroundColor: locationData.map((data) => data.color),
            pointRadius: POINT_RADIUS,
            clip: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { top: LOCATION_TOP_PADDING },
        },
        scales: {
          y: {
            min: BATTERY_MIN,
            max: LOCATION_Y,
            ticks: {
              stepSize: 20,
              includeBounds: false,
              callback: (value) => this.formatBatteryTick(value),
            },
          },
          x: {
            min: historyFrom,
            max: historyTo,
            type: 'time',
            time: {
              unit: timeUnit.unit,
              tooltipFormat: 'yyyy/MM/dd HH:mm:ss',
              displayFormats: {
                minute: 'MM/dd HH:mm',
                hour: 'MM/dd HH:mm',
                day: 'MM/dd',
              },
            },
            ticks: {
              stepSize: timeUnit.step,
              minRotation: 10,
            },
          },
        },
        plugins: {
          legend: { display: false },
          displayThresholdLine: {
            display: true,
            value: {
              minAlert: BATTERY_LOW_THRESHOLD_PERCENT,
            },
            color: {
              alert: this.markerColors['alert'],
              preAlert: this.markerColors['prealert'],
            },
            lineWidth: 1,
          },
          tooltip: {
            callbacks: {
              title: () => '',
              label: (context) => {
                const rawData = context.raw as {
                  x: string;
                  y: number;
                  locationTechType?: string;
                };
                const timeLabel = format(new Date(rawData.x), 'MM/dd HH:mm:ss');
                if (context.dataset.label === LOCATION_DATASET_LABEL) {
                  return `【${timeLabel}】${rawData.locationTechType || '-'}`;
                }
                return `【${timeLabel}】${rawData.y}%`;
              },
            },
          },
          zoom: {
            limits: {
              y: { min: BATTERY_MIN, max: LOCATION_Y },
            },
            pan: { enabled: true, mode: 'y' },
            zoom: { wheel: { enabled: true }, mode: 'y' },
          },
        },
        onClick: this.handleClick.bind(this),
      },
      plugins: [this.displayThresholdLine()],
    });
  }

  /** API履歴をグラフ表示用データへ変換する。 */
  private createBatteryLocationChartData(): {
    batteryData: { x: string; y: number }[];
    locationData: LocationChartPoint[];
  } {
    const batteryData = this.batteryHistoryList.map((result) => ({
      x: format(
        new Date(result.deviceReportedEpochtime),
        'yyyy-MM-dd HH:mm:ss',
      ),
      y: result.batteryPercentage,
    }));

    const locationData = this.locationHistoryList.flatMap((result) => {
      const location = result.location;
      if (
        location?.timeStamp == null ||
        location.latitude == null ||
        location.longitude == null
      ) {
        return [];
      }
      const locationTechType = location.locationTechType ?? '';
      return [
        {
          x: format(new Date(location.timeStamp), 'yyyy-MM-dd HH:mm:ss'),
          y: LOCATION_Y,
          rat: location.radioAccessTechnology ?? '-',
          locationTechType,
          color:
            LOCATION_COLORS[locationTechType.toUpperCase()] ??
            LOCATION_DEFAULT_COLOR,
          locationTimeStamp: location.timeStamp,
          longitude: location.longitude,
          latitude: location.latitude,
        },
      ];
    });

    return { batteryData, locationData };
  }

  /** バッテリー表示範囲外には軸ラベルを表示しない。 */
  private formatBatteryTick(value: string | number): string {
    return Number(value) <= BATTERY_MAX ? `${value}%` : '';
  }

  /**
   * センサータイプによってセンサーデータを抽出する
   * @param sensingType 選択されたセンサータイプ
   * @param selectedSensorHistory 複数センサの計測結果
   * @returns 選択されたセンサータイプのデータ,プラグイン使用可否
   */
  private processSensorData(
    sensingType: SensingType | '',
    selectedSensorHistory: SensingViewModel[],
  ): {
    sensorHistoryData: {
      x: string;
      y: number;
      actualMeasurement: number | undefined;
      eventType: string | undefined;
      eventTrigger: string | undefined;
      threshold: number | undefined;
    }[];
    enabled: boolean;
  } {
    const isTelemetryData = telemetryData.includes(sensingType);

    const sensorHistoryData = selectedSensorHistory
      .filter((data) => data?.eventAt != null)
      .map((data) => {
        const actualMeasurementValue = data.value;

        let yValue: number;

        if (isTelemetryData && data.value != null) {
          yValue = data.value;
        } else if (isTelemetryData) {
          yValue = NaN;
        } else {
          yValue = 1;
        }

        return {
          x: format(new Date(data.eventAt), 'yyyy-MM-dd HH:mm:ss'),
          y: yValue,
          actualMeasurement: actualMeasurementValue,
          eventType: data.eventType,
          eventTrigger: data.eventTrigger,
          status: data.status,
          threshold: data.threshold,
        };
      });

    // センサーデータが1件の場合、前後のデータを追加
    if (sensorHistoryData.length === 1) {
      const prevTime = format(
        subMinutes(new Date(sensorHistoryData[0].x), 5),
        'yyyy-MM-dd HH:mm',
      );
      const nextTime = format(
        addMinutes(new Date(sensorHistoryData[0].x), 5),
        'yyyy-MM-dd HH:mm',
      );

      const prevData = {
        x: prevTime,
        y: NaN,
        actualMeasurement: NaN,
        eventType: '',
        eventTrigger: '',
        status: '',
        threshold: undefined,
      };

      const nextData = {
        x: nextTime,
        y: NaN,
        actualMeasurement: NaN,
        eventType: '',
        eventTrigger: '',
        status: '',
        threshold: undefined,
      };

      // 配列に追加
      sensorHistoryData.unshift(prevData);
      sensorHistoryData.push(nextData);
    }

    return {
      sensorHistoryData,
      enabled: isTelemetryData,
    };
  }

  /**
   * チャートを作成する
   * @param ctx Canvas
   * @param sensingType 選択されたセンサータイプ
   * @param sensorHistoryData センサーの履歴情報
   */
  private createChart(
    ctx: CanvasRenderingContext2D,
    sensingType: SensingType | '',
    sensorHistoryData: {
      x: string;
      y: number;
      actualMeasurement: number | undefined;
      eventType: string | undefined;
      eventTrigger: string | undefined;
      threshold: number | undefined;
    }[],
    enabled: boolean,
  ) {
    const thresholdValue = this.getSensorThresholdValues(sensingType);

    const unit = this.sensorOptions[sensingType].unit;

    const detectionLabel = this.setLabel(sensingType);

    const historyFrom = format(
      new Date(this.historyFormGroup!.value.historyFrom),
      'yyyy-MM-dd HH:mm',
    );

    const historyTo = format(
      new Date(this.historyFormGroup!.value.historyTo),
      'yyyy-MM-dd HH:mm',
    );

    const observedData = this.getObservedData(sensorHistoryData);

    const timeUnit: UnitType = this.setTimeUnit(historyTo, historyFrom);

    const yScaleRange = this.setYScaleRange(
      sensingType,
      thresholdValue,
      observedData,
    );

    const lightExposureEventType: { [key: string]: { label: string } } = {
      LightToDark: { label: '明 → 暗 検出' },
      DarkToLight: { label: '暗 → 明 検出' },
      LightTooBright: { label: '明暗設定失敗' },
      OneTimeLightExposureDetected: { label: '光検出' },
    };

    const mcdDetectedEventTrigger: { [key: string]: { label: string } } = {
      InMotionToStationary: { label: '動 → 静 検知' },
      StationaryToInMotion: { label: '静 → 動 検知' },
    };

    const isSensor = telemetryData.includes(sensingType);
    const chartType = isSensor ? 'line' : 'scatter';

    this.chart = new Chart(ctx, {
      type: chartType,
      data: {
        datasets: [
          {
            data: sensorHistoryData,
            borderColor: 'rgb(111, 48, 160)',
            backgroundColor: 'rgba(111, 48, 160, 0.2)',
            borderWidth: 2,
            pointBackgroundColor: 'rgb(111, 48, 160)',
            pointRadius: chartType === 'scatter' ? 6 : 3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        aspectRatio: 10,
        scales: {
          y: {
            min: yScaleRange.min,
            max: yScaleRange.max,
            beginAtZero: false,
            grid: {
              color: 'rgba(0, 0, 0, 0.1)',
            },
            ticks: {
              stepSize: enabled ? undefined : 2,
              display: enabled,
              callback: (value) => {
                value = Number(value);
                if (Math.floor(value) === value) {
                  return value + unit;
                }
                return;
              },
            },
          },
          x: {
            min: historyFrom,
            max: historyTo,
            grid: {
              color: 'rgba(0, 0, 0, 0.1)',
            },
            type: 'time',
            time: {
              unit: timeUnit.unit,
              tooltipFormat: 'yyyy/MM/dd HH:mm:ss',
              displayFormats: {
                minute: 'MM/dd HH:mm',
                hour: 'MM/dd HH:mm',
                day: 'MM/dd',
              },
            },
            ticks: {
              stepSize: timeUnit.step,
              minRotation: 10,
            },
          },
        },
        plugins: {
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            bodyFont: {
              size: 14,
            },
            titleFont: {
              size: 16,
              weight: 'bold',
            },
            callbacks: {
              title: function () {
                return '';
              },
              label: function (context) {
                const rawData = context.raw as {
                  x: string | null;
                  y: number | undefined;
                  actualMeasurement: number | undefined;
                  eventType: string | undefined;
                  eventTrigger: string | undefined;
                  threshold: number | undefined;
                };

                const time = rawData.x ? new Date(rawData.x) : null;
                const timeLabel = time ? format(time, 'MM/dd HH:mm:ss') : '';
                let label = `【${timeLabel}】`;

                if (sensingType === 'lightExposure') {
                  const keys = Object.keys(lightExposureEventType);

                  keys.forEach((key) => {
                    if (rawData.eventType === key) {
                      label += lightExposureEventType[key].label;
                      label += `( ${rawData.actualMeasurement !== null ? rawData.actualMeasurement : '-'}${unit} )`;
                    }
                  });

                  return label;
                }

                if (sensingType === 'mcdDetected') {
                  const keys = Object.keys(mcdDetectedEventTrigger);

                  keys.forEach((key) => {
                    if (rawData.eventTrigger === key) {
                      label += mcdDetectedEventTrigger[key].label;
                    }
                  });

                  return label;
                }

                if (detectionLabel === '落下検知') {
                  label += detectionLabel;
                  return label;
                } else if (detectionLabel === '衝撃検知') {
                  // 衝撃検知の場合、アラート発生時の閾値をラベルに表示する。
                  const pointThreshold = rawData.threshold ?? '-';
                  label += detectionLabel + `( ${pointThreshold}${unit})`;
                  return label;
                }
                label += rawData.y + unit;
                return label;
              },
            },
          },
          legend: {
            display: false,
          },
          displayThresholdLine: {
            display: enabled,
            value: {
              maxAlert: thresholdValue.alertMax,
              minAlert: thresholdValue.alertMin,
              maxPreAlert: thresholdValue.preAlertMax,
              minPreAlert: thresholdValue.preAlertMin,
            },
            color: {
              alert: this.markerColors['alert'],
              preAlert: this.markerColors['prealert'],
            },
            lineWidth: 1,
          },
          zoom: {
            limits: {
              y: {
                min: yScaleRange.min,
                max: yScaleRange.max,
              },
            },
            pan: {
              enabled: true,
              mode: 'y',
            },
            zoom: {
              wheel: {
                enabled: true,
              },
              mode: 'y',
            },
          },
        },
        onClick: this.handleClick.bind(this),
      },
      plugins: [this.displayThresholdLine()],
    });
  }

  /**
   * 選択されたセンサーの実測値データを取得する
   * @param sensorHistoryData センサー履歴データ
   * @returns 時刻開始と終了・最大値と最小値
   */
  private getObservedData(sensorHistoryData: { x: string; y: number }[]) {
    const sensorHistoryTime = sensorHistoryData
      ?.map((data) => data.x)
      .filter((value) => value !== null && value !== undefined);

    const xValueMax =
      sensorHistoryTime.length > 0
        ? sensorHistoryTime[sensorHistoryTime.length - 1]
        : '';
    const xValueMin = sensorHistoryTime.length > 0 ? sensorHistoryTime[0] : '';

    const sensorHistoryValue = sensorHistoryData
      ?.map((data) => data.y)
      .filter(
        (value) =>
          value !== null && value !== undefined && !Number.isNaN(value),
      );

    const yValueMax = Math.max(...sensorHistoryValue);
    const yValueMin = Math.min(...sensorHistoryValue);

    return {
      xValueMax,
      xValueMin,
      yValueMax,
      yValueMin,
    };
  }

  /**
   * 時刻スケールに使用する時刻単位とステップ単位を設定する
   * @param xScaleMax X軸の最大
   * @param xScaleMin X軸の最小
   * @returns 時刻単位とステップ単位
   */
  private setTimeUnit(xScaleMax: string, xScaleMin: string): UnitType {
    const minTime = new Date(xScaleMin);
    const maxTime = new Date(xScaleMax);
    const timeDiff = (maxTime.getTime() - minTime.getTime()) / (1000 * 60 * 60);

    const timeUnits: {
      maxDiff: number;
      unit: UnitType['unit'];
      step: number;
    }[] = [
      { maxDiff: 0.5, unit: 'minute', step: 5 },
      { maxDiff: 3, unit: 'minute', step: 5 },
      { maxDiff: 12, unit: 'minute', step: 30 },
      { maxDiff: 24, unit: 'hour', step: 1 },
      { maxDiff: 47, unit: 'hour', step: 2 },
      { maxDiff: 168, unit: 'hour', step: 8 },
      { maxDiff: Infinity, unit: 'day', step: 1 },
    ];

    for (const timeUnit of timeUnits) {
      if (timeDiff <= timeUnit.maxDiff) {
        return {
          unit: timeUnit.unit,
          step: timeUnit.step,
        };
      }
    }

    // デフォルトの値を返す
    return {
      unit: 'day',
      step: 1,
    };
  }

  /**
   * 選択されたセンサータイプの閾値を取得する
   * @param sensingType 選択されたセンサータイプ
   * @returns
   */
  private getSensorThresholdValues(sensingType: SensingType | ''): {
    alertMax: number | undefined;
    alertMin: number | undefined;
    preAlertMax: number | undefined;
    preAlertMin: number | undefined;
  } {
    const thresholdValue: {
      alertMax: number | undefined;
      alertMin: number | undefined;
      preAlertMax: number | undefined;
      preAlertMin: number | undefined;
    } = {
      alertMax: undefined,
      alertMin: undefined,
      preAlertMax: undefined,
      preAlertMin: undefined,
    };

    switch (sensingType) {
      case 'temperature':
        thresholdValue.alertMax =
          this.sensorThresholdInfo.temperature?.AlertMax;
        thresholdValue.alertMin =
          this.sensorThresholdInfo.temperature?.AlertMin;
        thresholdValue.preAlertMax =
          this.sensorThresholdInfo.temperature?.PrealertMax;
        thresholdValue.preAlertMin =
          this.sensorThresholdInfo.temperature?.PrealertMin;
        break;
      case 'pressure':
        thresholdValue.alertMax = this.sensorThresholdInfo.pressure?.AlertMax;
        thresholdValue.alertMin = this.sensorThresholdInfo.pressure?.AlertMin;
        thresholdValue.preAlertMax =
          this.sensorThresholdInfo.pressure?.PrealertMax;
        thresholdValue.preAlertMin =
          this.sensorThresholdInfo.pressure?.PrealertMin;
        break;
      case 'humidity':
        thresholdValue.alertMax = this.sensorThresholdInfo.humidity?.AlertMax;
        thresholdValue.alertMin = this.sensorThresholdInfo.humidity?.AlertMin;
        thresholdValue.preAlertMax =
          this.sensorThresholdInfo.humidity?.PrealertMax;
        thresholdValue.preAlertMin =
          this.sensorThresholdInfo.humidity?.PrealertMin;
        break;
      case 'tilt':
        thresholdValue.alertMax = this.sensorThresholdInfo.tilt?.AlertMax;
        break;
      case 'light':
        thresholdValue.alertMax = this.sensorThresholdInfo.light?.AlertMax;
        thresholdValue.alertMin = this.sensorThresholdInfo.light?.AlertMin;
        thresholdValue.preAlertMax =
          this.sensorThresholdInfo.light?.PrealertMax;
        thresholdValue.preAlertMin =
          this.sensorThresholdInfo.light?.PrealertMin;
        break;
      default:
        thresholdValue.alertMax = undefined;
        break;
    }
    return thresholdValue;
  }

  /**
   * Y軸の上限/下限の値を設定する
   * @param sensingType 選択されたセンサータイプ
   * @param thresholdValue 閾値オブジェクト
   * @returns
   */
  private setYScaleRange(
    sensingType: SensingType | '',
    thresholdValue: {
      alertMax: number | undefined;
      alertMin: number | undefined;
      preAlertMax: number | undefined;
      preAlertMin: number | undefined;
    },
    observedData: {
      xValueMax: string;
      xValueMin: string;
      yValueMax: number;
      yValueMin: number;
    },
  ): { min: number; max: number } {
    const yValueMax = Number.isNaN(observedData.yValueMax)
      ? -Infinity
      : observedData.yValueMax;

    const yValueMin = Number.isNaN(observedData.yValueMin)
      ? Infinity
      : observedData.yValueMin;

    // アラート・ワーニング・実測値を比較した最大値
    const yMax = Math.max(
      yValueMax,
      thresholdValue.alertMax ?? -Infinity,
      thresholdValue.preAlertMax ?? -Infinity,
    );

    // アラート・ワーニング・実測値を比較した最小値
    const yMin = Math.min(
      yValueMin,
      thresholdValue.alertMin ?? Infinity,
      thresholdValue.preAlertMin ?? Infinity,
    );

    // Yスケールの計算
    let { min: yScaleMin, max: yScaleMax } = this.calculateYScaleValues(
      yMax,
      yMin,
    );

    // イベント検知の場合
    if (telemetryData.includes(sensingType)) {
      if (sensingType === 'tilt') {
        yScaleMax = yScaleMax > 180 ? 180 : yScaleMax;
        yScaleMin = yScaleMin < 0 ? 0 : yScaleMin;
      } else if (sensingType === 'pressure') {
        yScaleMin = yScaleMin < 0 ? 0 : yScaleMin;
      } else if (sensingType === 'humidity') {
        yScaleMax = yScaleMax > 100 ? 100 : yScaleMax;
        yScaleMin = yScaleMin < 0 ? 0 : yScaleMin;
      } else if (sensingType === 'light') {
        yScaleMin = yScaleMin < 0 ? 0 : yScaleMin;
      }
      return { min: yScaleMin, max: yScaleMax };
    } else {
      yScaleMax = 2;
      yScaleMin = 0;
      return { min: yScaleMin, max: yScaleMax };
    }
  }

  /**
   * Y軸のスケールをY軸の上限と下限の差分から算出
   * @param yMax Y軸の表示上限
   * @param yMin Y軸の表示下限
   * @returns 算出後のY軸上限と下限
   */
  private calculateYScaleValues(
    yMax: number,
    yMin: number,
  ): { min: number; max: number } {
    // Y軸の最大値が存在する時、最小値がInfinityの場合エラーになるため、0を設定
    if (isFinite(yMax) && !isFinite(yMin)) {
      yMin = 0;
    }

    const diff = yMax - yMin;

    let yScaleMax: number;
    let yScaleMin: number;

    if (diff === 0) {
      yScaleMax = Math.ceil((yMax + yMax * 0.1) / 1) * 1;
      yScaleMin = Math.floor((yMin - yMin * 0.1) / 1) * 1;
    } else if (diff < 10) {
      yScaleMax = Math.ceil((yMax + diff * 0.2) / 1) * 1;
      yScaleMin = Math.floor((yMin - diff * 0.2) / 1) * 1;
    } else if (diff < 100) {
      yScaleMax = Math.ceil((yMax + diff * 0.2) / 10) * 10;
      yScaleMin = Math.floor((yMin - diff * 0.2) / 10) * 10;
    } else {
      yScaleMax = Math.ceil((yMax + diff * 0.2) / 100) * 100;
      yScaleMin = Math.floor((yMin - diff * 0.2) / 100) * 100;
    }

    return { min: yScaleMin, max: yScaleMax };
  }

  /**
   * アラート閾値線を描画するプラグイン
   * @returns
   */
  private displayThresholdLine() {
    return {
      id: 'displayThresholdLine',
      beforeDraw(
        chart: Chart,
        args: {
          cancelable?: boolean;
          cancelled?: boolean;
        },
        options: {
          display: boolean;
          value: {
            maxAlert: number;
            minAlert: number;
            maxPreAlert: number;
            minPreAlert: number;
          };
          color: {
            alert: string;
            preAlert: string;
          };
          lineWidth: number;
        },
      ) {
        if (!options.display) {
          // プラグインが無効な場合は何もせずに戻る
          return;
        }

        const {
          ctx,
          scales: { x, y },
        } = chart;

        const alertLines = [
          options.value.maxAlert,
          options.value.minAlert,
          options.value.maxPreAlert,
          options.value.minPreAlert,
        ];
        alertLines.forEach((alertLineValue, index) => {
          if (alertLineValue !== null && alertLineValue !== undefined) {
            const yValue = y.getPixelForValue(alertLineValue);

            ctx.save();
            ctx.beginPath();
            ctx.moveTo(x.left, yValue);
            ctx.lineTo(x.right, yValue);
            if (index > 1) {
              ctx.strokeStyle = options.color['preAlert'];
            } else {
              ctx.strokeStyle = options.color['alert'];
            }
            ctx.lineWidth = options.lineWidth;
            ctx.setLineDash([8]);
            ctx.stroke();
            ctx.restore();
          }
        });
      },
    };
  }

  private handleClick(event: ChartEvent, elements: ActiveElement[]) {
    if (!event || this.sensingType == null) return;

    if (this.sensingType === 'battery') {
      const selectedElement = elements[0];
      if (selectedElement == null) return;
      const dataset = this.chart.data.datasets[selectedElement.datasetIndex];
      if (dataset.label !== LOCATION_DATASET_LABEL) return;
      const point = dataset.data[selectedElement.index] as LocationChartPoint;
      const selection: LocationSelectionViewModel = {
        locationTimeStamp: point.locationTimeStamp,
        longitude: point.longitude,
        latitude: point.latitude,
      };
      this.chartPointsChanged.emit(selection);
      return;
    }

    let selectedChart: SensingViewModel[];
    let raw: {
      x: string;
      y: number;
      actualMeasurement: number | undefined;
      eventType: string | undefined;
      status: string | undefined;
    };

    if (elements.length > 0) {
      const eventType = [
        'fallDetected',
        'mcdDetected',
        'lightExposure',
        'shockDetected',
      ];

      // ステータスごとに優先順位を設定
      const statusPriority = ['Alert', 'PreAlert', '', null];

      for (const priority of statusPriority) {
        const data = elements.find(
          (element) =>
            (element.element as unknown as CustomElement).$context.raw
              .status === priority,
        );
        if (data) {
          raw = (data.element as unknown as CustomElement).$context.raw;
          break;
        }
      }

      if (eventType.includes(this.sensingType)) {
        selectedChart = this.selectedSensorHistory.filter(
          (sensingData): sensingData is SensingViewModel =>
            format(new Date(sensingData.eventAt), 'yyyy-MM-dd HH:mm:ss') ===
              raw.x &&
            sensingData.value === raw.actualMeasurement &&
            sensingData.status === raw.status,
        );
      } else {
        selectedChart = this.selectedSensorHistory.filter(
          (sensingData): sensingData is SensingViewModel =>
            format(new Date(sensingData.eventAt), 'yyyy-MM-dd HH:mm:ss') ===
              raw.x &&
            sensingData.value === raw.y &&
            sensingData.status === raw.status,
        );
      }
      this.chartPointsChanged.emit(selectedChart[0]);
    }
  }

  private setLabel(sensingType: SensingType | ''): string | undefined {
    if (
      sensingType === 'fallDetected' ||
      sensingType === 'mcdDetected' ||
      sensingType === 'shockDetected'
    ) {
      return this.sensorOptions[sensingType].label;
    }
    return undefined;
  }
  /**
   * チャートの破棄を実行
   */
  private destroyChart() {
    if (this.chart) {
      this.chart.destroy();
    }
  }
}
